import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Container, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import { FaRobot } from "react-icons/fa";
import Cookies from "universal-cookie";
import axios from "axios";

const COGNITO_CLIENT_ID = "434nnngm1kfvo0fm4umg3v8bud";
const COGNITO_REGION = "us-east-2";
const COGNITO_URL = `https://cognito-idp.${COGNITO_REGION}.amazonaws.com/`;

function cognitoPost(target, body) {
  return axios.post(COGNITO_URL, body, {
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": `AWSCognitoIdentityProviderService.${target}`,
    },
  });
}

const SIGNUP_ERRORS = {
  UsernameExistsException: "An account with this username already exists.",
  InvalidPasswordException: null, // use Cognito's message — it describes the policy
  InvalidParameterException: null,
  AliasExistsException: "An account with this email already exists.",
};

const VERIFY_ERRORS = {
  CodeMismatchException: "Incorrect verification code.",
  ExpiredCodeException: "Code has expired. Click 'Resend code' to get a new one.",
  NotAuthorizedException: "This account is already confirmed. Please sign in.",
};

const Signup = () => {
  const navigate = useNavigate();

  // phase: "register" | "verify"
  const [phase, setPhase] = useState("register");

  // register form
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // verify form
  const [code, setCode] = useState("");
  const [resent, setResent] = useState(false);

  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // ── Phase 1: Register ────────────────────────────────────────────────────────

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsLoading(true);
    try {
      await cognitoPost("SignUp", {
        ClientId: COGNITO_CLIENT_ID,
        Username: username.trim(),
        Password: password,
        UserAttributes: [{ Name: "email", Value: email.trim() }],
      });
      setPhase("verify");
    } catch (err) {
      const type = err.response?.data?.__type?.split("#").pop();
      const msg = SIGNUP_ERRORS[type];
      setError(msg !== undefined ? (msg ?? err.response?.data?.message) : "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // ── Phase 2: Verify ──────────────────────────────────────────────────────────

  const handleVerify = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await cognitoPost("ConfirmSignUp", {
        ClientId: COGNITO_CLIENT_ID,
        Username: username.trim(),
        ConfirmationCode: code.trim(),
      });

      // Auto sign-in after confirmation
      const res = await cognitoPost("InitiateAuth", {
        AuthFlow: "USER_PASSWORD_AUTH",
        ClientId: COGNITO_CLIENT_ID,
        AuthParameters: {
          USERNAME: username.trim(),
          PASSWORD: password,
        },
      });

      const { AccessToken } = res.data.AuthenticationResult;
      new Cookies().set("jwt", AccessToken, { path: "/", secure: true });
      sessionStorage.setItem("jwt", AccessToken);
      navigate("/app");
    } catch (err) {
      const type = err.response?.data?.__type?.split("#").pop();
      const msg = VERIFY_ERRORS[type];
      setError(msg ?? "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    setResent(false);
    try {
      await cognitoPost("ResendConfirmationCode", {
        ClientId: COGNITO_CLIENT_ID,
        Username: username.trim(),
      });
      setResent(true);
    } catch (err) {
      setError("Could not resend code. Please try again.");
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
      <Card style={{ width: "100%", maxWidth: 420 }} className="p-4 shadow-sm">
        <div className="text-center mb-4">
          <FaRobot size={36} className="text-primary mb-2" />
          {phase === "register" ? (
            <>
              <h4 className="fw-bold mb-0">Create your account</h4>
              <p className="text-muted small mt-1">Start automating your trades today</p>
            </>
          ) : (
            <>
              <h4 className="fw-bold mb-0">Verify your email</h4>
              <p className="text-muted small mt-1">
                We sent a 6-digit code to <strong>{email}</strong>
              </p>
            </>
          )}
        </div>

        {error && (
          <Alert variant="danger" className="py-2 small">
            {error}
          </Alert>
        )}

        {resent && (
          <Alert variant="success" className="py-2 small">
            Verification code resent.
          </Alert>
        )}

        {phase === "register" ? (
          <Form onSubmit={handleRegister}>
            <Form.Group className="mb-3" controlId="signupUsername">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                placeholder="Choose a username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="signupEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="signupPassword">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-4" controlId="signupConfirmPassword">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </Form.Group>

            <Button type="submit" variant="primary" className="w-100" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Creating account…
                </>
              ) : (
                "Create Account"
              )}
            </Button>
          </Form>
        ) : (
          <Form onSubmit={handleVerify}>
            <Form.Group className="mb-4" controlId="signupCode">
              <Form.Label>Verification Code</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                placeholder="6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoFocus
                maxLength={6}
              />
            </Form.Group>

            <Button type="submit" variant="primary" className="w-100" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Verifying…
                </>
              ) : (
                "Verify & Sign In"
              )}
            </Button>

            <p className="text-center text-muted small mt-3 mb-0">
              Didn't receive it?{" "}
              <Button variant="link" className="p-0 small text-primary" onClick={handleResend}>
                Resend code
              </Button>
            </p>
          </Form>
        )}

        <p className="text-center text-muted small mt-4 mb-0">
          Already have an account?{" "}
          <Link to="/login" className="text-primary text-decoration-none">
            Sign in
          </Link>
        </p>
      </Card>
    </Container>
  );
};

export default Signup;
